// Can a notification actually reach this person, and if not, why not?
//
// Ported from the Andon app, where it was written because push used to be a
// thing that either happened or quietly did not. Every failure path in the
// registration was a bare `return`: no keys on the server, a denied
// permission, a browser with no PushManager, a subscribe the push service
// refused. All of them left the app looking exactly like the working case —
// a signed-in responder, a floor screen, no indication whatsoever that the
// phone in their pocket is never going to buzz. The first anybody finds out
// is a line down for twenty minutes and a manager who says they were never
// told. So the failure gets a name, and the names are the ones a person can
// act on.
//
// WHAT IS KNOWABLE AND WHAT IS NOT
// --------------------------------
// This module can only see the web's half of the chain:
//
//   knowable    the server has VAPID keys · the browser has a PushManager ·
//               this is a home-screen app rather than an iOS tab ·
//               Notification.permission · a subscription exists and the server
//               acknowledged it
//
//   NOT knowable  Do Not Disturb · a Focus mode · notifications switched off
//               for the browser itself in the OS · an Android battery
//               optimisation that has frozen the push service · a corporate
//               MDM profile · whether the push service actually delivered
//
// Everything in the first list is a PRECONDITION. All of them can be satisfied
// on a phone that still shows nothing, which is why `ok` here is worded as "as
// far as this app can tell" and why the notifications sheet offers a test push:
// past this point the only honest check is to send one and look at the phone.
//
// The bias, as with the install banner, is toward saying something. A responder
// told twice that notifications are off has been mildly annoyed; a responder
// never told is a responder the ladder cannot reach.

import type { StringKey } from '@/i18n/strings';
import { isIos, isStandalone } from '@/lib/installPrompt';

export type PushHealth =
  /** Every precondition we can see is met. Not a promise — see the header. */
  | 'ok'
  /** Never been asked. One tap fixes it, and the tap has to be a real one. */
  | 'ask'
  /** Denied. The app can never ask again; only the OS or browser can undo it. */
  | 'blocked'
  /** Allowed, but there is no live subscription the server has acknowledged. */
  | 'unregistered'
  /** iOS in a tab: Web Push does not exist until the app is on the home screen. */
  | 'needs-install'
  /** No service worker or no PushManager. Nothing to offer. */
  | 'unsupported'
  /** Push is not configured on this deployment, or this is not a session it can address. */
  | 'off';

export interface PushEnv {
  /** The server has VAPID keys and this is a session allowed to subscribe. */
  serverPush: boolean;
  /** This browser has both a service worker and a PushManager. */
  supported: boolean;
  /** iOS or iPadOS, where push is a home-screen-app-only feature. */
  ios: boolean;
  /** Running as an installed app rather than in a browser tab. */
  standalone: boolean;
  permission: NotificationPermission;
  /** A live subscription exists AND this server has been told about it. */
  registered: boolean;
}

/**
 * Turn what we can observe into the one thing worth saying about it.
 *
 * Split from the observing so the ordering can be tested, because the ordering
 * is the whole design. Two of these decisions are load-bearing:
 *
 * `needs-install` is checked BEFORE `unsupported`. On iOS in a tab there is
 * genuinely no PushManager, so the naive answer is "your browser does not
 * support notifications" — which is false, and worse, it is unactionable. The
 * browser supports them perfectly well once the app is on the home screen, and
 * that is a thing the person holding the phone can do in ten seconds.
 *
 * `blocked` is checked BEFORE `registered`. A denied permission is not a
 * missing subscription that could be retried; it is a door the app is not
 * allowed to knock on again, and offering an "Enable" button that provably does
 * nothing is worse than saying plainly where the switch actually is.
 */
export function classifyPush(env: PushEnv): PushHealth {
  if (!env.serverPush) return 'off';
  if (env.ios && !env.standalone) return 'needs-install';
  if (!env.supported) return 'unsupported';
  if (env.permission === 'denied') return 'blocked';
  if (env.permission === 'default') return 'ask';
  return env.registered ? 'ok' : 'unregistered';
}

/**
 * The states somebody can do something about, here, now.
 *
 * The other four are facts about the device or the deployment — an iOS tab, a
 * browser with no PushManager, a server with no keys, a working subscription —
 * and none of them is a reason to interrupt anybody.
 */
export type PushProblem = Extract<PushHealth, 'ask' | 'blocked' | 'unregistered'>;

/** Is this a state worth interrupting somebody about? A guard, so callers can key off it. */
export function pushNeedsAttention(health: PushHealth): health is PushProblem {
  return health === 'ask' || health === 'blocked' || health === 'unregistered';
}

/**
 * Read the half of the environment that does not need a service worker.
 *
 * `pwa.ts` supplies the other half — the subscription and whether the server
 * took it — because that is where the registration lives.
 */
export function readPushEnv(serverPush: boolean, registered: boolean): PushEnv {
  const supported =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window;
  return {
    serverPush,
    supported,
    ios: isIos(),
    standalone: isStandalone(),
    // Reading `Notification.permission` on a browser without it throws, and
    // `supported` is already false in that case — 'default' is the value that
    // makes `classifyPush` fall through to `unsupported` rather than past it.
    permission: supported ? Notification.permission : 'default',
    registered,
  };
}

/**
 * What to tell somebody, and whether there is anything to press — as
 * translation keys, because two surfaces say it in three languages.
 *
 * Lives here rather than in the banner because the banner that interrupts and
 * the notifications sheet somebody opens on purpose must not drift into two
 * different accounts of the same fault.
 *
 * `blocked` gets instructions and no button, because there is no button. A
 * denied permission cannot be re-requested by the page; only the OS or the
 * browser can undo it, and an "Enable" that provably does nothing is worse than
 * saying plainly where the switch is. Which switch that is depends on whether
 * this is an installed app — one has its own row in the phone's notification
 * settings and one is a per-site browser permission — and naming the wrong one
 * sends somebody hunting through Settings for something that is not there.
 */
export function pushCopy(
  health: PushHealth,
  installed: boolean,
): { title: StringKey; body: StringKey; action: StringKey | null } {
  switch (health) {
    case 'blocked':
      return { title: 'pushBlockedTitleT', body: installed ? 'pushBlockedInstalledBodyT' : 'pushBlockedTabBodyT', action: null };
    case 'unregistered':
      return { title: 'pushUnregisteredTitleT', body: 'pushUnregisteredBodyT', action: 'retry' };
    case 'needs-install':
      return { title: 'pushNeedsInstallTitleT', body: 'pushNeedsInstallBodyT', action: null };
    case 'unsupported':
      return { title: 'pushUnsupportedTitleT', body: 'pushUnsupportedBodyT', action: null };
    case 'off':
      // A deployment with no keys, or a session with nobody behind it to
      // address: a kiosk waiting on a PIN, or an account with no employee.
      return { title: 'pushOffTitleT', body: 'pushOffBodyT', action: null };
    case 'ok':
      // Carefully not a promise. Everything past this point — Do Not Disturb,
      // a Focus mode, the OS switch for the browser itself, an Android battery
      // optimisation — is invisible to a web page, which is why the sheet puts
      // a test button directly underneath this line.
      return { title: 'pushOkTitleT', body: 'pushOkBodyT', action: null };
    case 'ask':
      return { title: 'pushAskTitleT', body: 'pushAskBodyT', action: 'turnOnT' };
  }
}

// ── the shared reading, so every surface says the same thing ────────────────
//
// Deliberately a module rather than store state, for the same reason
// `lib/installPrompt` is: this is answered by the browser and the service
// worker, not by us, and the answer changes at moments React is not involved
// in — a permission granted in a system dialog, a subscription dropped by the
// push service, an app adopted into standalone mid-session.

let current: PushHealth = 'off';
const listeners = new Set<() => void>();

export function pushHealth(): PushHealth {
  return current;
}

export function setPushHealth(next: PushHealth): void {
  if (next === current) return;
  current = next;
  for (const fn of listeners) fn();
}

export function subscribePushHealth(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * How long a "not now" on the push banner is respected.
 *
 * A week — shorter than the install banner's month, and for a reason: this is
 * not cosmetic. Somebody who dismissed it is somebody whose phone stays
 * silent, so it is allowed to come back at the start of the next week's shift.
 */
export const PUSH_SNOOZE_MS = 7 * 24 * 3_600_000;

const SNOOZE_KEY = 'sf.push.snoozed';

/**
 * Does a stored "not now" still stand?
 *
 * Pure, and separated from storage and from the clock, so the decision is
 * testable without a DOM. Every unreadable answer resolves to "ask again" —
 * being asked twice costs a tap, and never being asked costs an andon.
 */
export function pushSnoozeStands(raw: string | null, now: number): boolean {
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  // A timestamp in the future means the clock moved backwards, or somebody
  // edited it by hand. Either way it must not snooze this forever.
  if (at > now) return false;
  return now - at < PUSH_SNOOZE_MS;
}

export function pushSnoozed(): boolean {
  try {
    return pushSnoozeStands(localStorage.getItem(SNOOZE_KEY), Date.now());
  } catch {
    // Private mode, or storage disabled. Asking again is the safe direction.
    return false;
  }
}

export function snoozePush(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
  } catch {
    /* ignore — worst case it asks again next load */
  }
}

/**
 * Forget the snooze.
 *
 * Called when push starts working, so that a device which later loses its
 * subscription — the push service dropped it, the server was restarted and
 * nothing re-registered — is told immediately rather than sitting silent for
 * the remainder of a dismissal it earned while it was healthy.
 */
export function clearPushSnooze(): void {
  try {
    localStorage.removeItem(SNOOZE_KEY);
  } catch {
    /* ignore */
  }
}
